import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Collapse, List, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';

// third-party
import { DownOutlined, RightOutlined } from '@ant-design/icons';

// project import
import NavItem from './NavItem';
import { activeItem, openDrawer } from 'store/reducers/menu';

// ==============================|| NAVIGATION - COLLAPSE ||============================== //

const NavCollapse = ({ item, level = 1 }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { drawerOpen, openItem } = useSelector((state) => state.menu);

  const childItems = item.children || [];
  const hasActiveChild = useMemo(
    () => childItems.some((child) => child.url && pathname.startsWith(child.url)),
    [childItems, pathname]
  );

  const [open, setOpen] = useState(hasActiveChild);

  useEffect(() => {
    if (hasActiveChild) {
      setOpen(true);
    }
  }, [hasActiveChild]);

  const itemHandler = () => {
    if (!drawerOpen) {
      dispatch(openDrawer({ drawerOpen: true }));
      setOpen(true);
    } else {
      setOpen((prev) => !prev);
    }

    dispatch(activeItem({ openItem: [item.id] }));
  };

  const Icon = item.icon;
  const itemIcon = item.icon ? <Icon style={{ fontSize: drawerOpen ? '1rem' : '1.25rem' }} /> : false;
  const isSelected = openItem.includes(item.id) && !hasActiveChild;
  const textColor = 'rgba(255,255,255,0.72)';
  const iconSelectedColor = '#fff';

  return (
    <>
      <ListItemButton
        onClick={itemHandler}
        selected={isSelected}
        sx={{
          zIndex: 1201,
          pl: drawerOpen ? `${level * 28}px` : 0,
          pr: drawerOpen ? 1 : 0,
          py: !drawerOpen && level === 1 ? 1.25 : 1,
          ...(drawerOpen && {
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.08)'
            },
            '&.Mui-selected': {
              bgcolor: 'primary.main',
              borderRight: 'none',
              borderRadius: '0 8px 8px 0',
              mr: 1,
              color: '#fff',
              '&:hover': {
                color: '#fff',
                bgcolor: 'primary.dark'
              }
            }
          }),
          ...(!drawerOpen && {
            justifyContent: 'center',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.08)'
            },
            '&.Mui-selected': {
              bgcolor: 'primary.main',
              borderRadius: 1.5,
              '&:hover': {
                bgcolor: 'primary.dark'
              }
            }
          })
        }}
      >
        {itemIcon && (
          <ListItemIcon
            sx={{
              minWidth: drawerOpen ? 28 : 'auto',
              color: isSelected ? iconSelectedColor : textColor,
              ...(!drawerOpen && {
                borderRadius: 1.5,
                width: 36,
                height: 36,
                margin: 0,
                alignItems: 'center',
                justifyContent: 'center'
              }),
              ...(!drawerOpen &&
                isSelected && {
                  bgcolor: 'primary.main'
                })
            }}
          >
            {itemIcon}
          </ListItemIcon>
        )}

        {drawerOpen && (
          <>
            <ListItemText
              primary={
                <Typography variant="h6" sx={{ color: isSelected ? iconSelectedColor : textColor }}>
                  {item.title}
                </Typography>
              }
            />
            {open ? <DownOutlined style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }} /> : <RightOutlined style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }} />}
          </>
        )}
      </ListItemButton>

      {drawerOpen && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List disablePadding>
            {childItems.map((child) => {
              if (child.type === 'item') {
                return <NavItem key={child.id} item={child} level={level + 1} />;
              }

              return <NavCollapse key={child.id} item={child} level={level + 1} />;
            })}
          </List>
        </Collapse>
      )}
    </>
  );
};

NavCollapse.propTypes = {
  item: PropTypes.object,
  level: PropTypes.number
};

export default NavCollapse;
